// frontend/src/components/SearchInterface.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button, message, Spin, Switch } from 'antd';
import { SearchOutlined, LogoutOutlined, CalculatorOutlined, TeamOutlined, CloudUploadOutlined } from '@ant-design/icons';
import ProjectUpload from './Ingest/ProjectUpload';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { dataAPI, estimationAPI } from '../services/api';
import Sidebar from './Sidebar';
import SearchOverview from './SearchOverview';
import Results from './Results';
import './SearchInterface.css';

const { Header, Sider, Content } = Layout;

const SearchInterface = () => {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const canUpload = hasPermission('datenimport');
  const [uploadOpen, setUploadOpen] = useState(false);
  const navigate = useNavigate();
  const canEstimate = hasPermission('kostenschaetzung');

  const [filters, setFilters] = useState({
    codes: [],
    buildingType: ['1.1 Wohnen'],
    kanton: ['Zürich'],
    sizeRange: [1000, 15000],
    sizeRangeEnabled: false,
    volRange: [40000, 70000],
    volRangeEnabled: false,
    oberirdischRange: [0, 5000],
    oberirdischRangeEnabled: false,
    yearRange: [2020, 2024],
    yearRangeEnabled: false,
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ebkpCodes, setEbkpCodes] = useState([]);
  const [autoSearch, setAutoSearch] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const debounceTimer = useRef(null);

  // Only load eBKP codes if user is authenticated
  useEffect(() => {
    if (user) {
      loadEbkpCodes();
    }
  }, [user]);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }

    if (autoSearch) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        handleSearch();
      }, 800);

      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }
  }, [filters, autoSearch]);

  const loadEbkpCodes = async () => {
    try {
      const response = await dataAPI.getEbkpCodes();
      setEbkpCodes(response.data.codes);
    } catch (error) {
      message.error('Fehler beim Laden der eBKP Codes');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchRequest = {
        codes: filters.codes,
        building_type: filters.buildingType,
        kanton: filters.kanton,
        size_range: filters.sizeRangeEnabled ? filters.sizeRange : null,
        size_range_enabled: filters.sizeRangeEnabled,
        vol_range: filters.volRangeEnabled ? filters.volRange : null,
        vol_range_enabled: filters.volRangeEnabled,
        oberirdisch_range: filters.oberirdischRangeEnabled ? filters.oberirdischRange : null,
        oberirdisch_range_enabled: filters.oberirdischRangeEnabled,
        year_range: filters.yearRangeEnabled ? filters.yearRange : null,
        year_range_enabled: filters.yearRangeEnabled,
      };

      const response = await dataAPI.search(searchRequest);
      setResults(response.data);

      if (response.data.success) {
        if (!autoSearch) {
          message.success(`${response.data.total_projects} Projekte gefunden`);
        }
      } else {
        message.warning(response.data.message);
      }
    } catch (error) {
      message.error('Fehler bei der Suche');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEstimation = () => {
    // Navigate to cost estimation page with search results and filters
    navigate('/estimate', {
      state: {
        searchResults: results,
        filters: filters,
        from: '/search'
      }
    });
  };

  /**
   * Handle continuing a saved estimation
   */
  const handleContinueEstimation = async (estimationId) => {
    try {
      setLoading(true);

      // Fetch the saved estimation details
      const response = await estimationAPI.get(estimationId);
      const savedEstimation = response.data;

      console.log('Loading saved estimation:', savedEstimation);

      // Navigate to cost estimation page with estimation ID and name
      navigate('/estimate', {
        state: {
          estimationId: savedEstimation.id,
          estimationName: savedEstimation.name,
          from: '/search'
          // Don't pass search results or filters - they'll be loaded in CostEstimation component
        }
      });

      message.success(`Kostenschätzung "${savedEstimation.name}" wird geladen...`);

    } catch (error) {
      console.error('Error loading estimation:', error);
      message.error('Fehler beim Laden der Kostenschätzung');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={300}
        theme="light"
        className="wz-sider"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="wz-sider-inner">
          <div className="wz-user-card">
            <div className="wz-label">Angemeldet als</div>
            <div className="wz-user-chip">
              <span className="wz-avatar">{(user?.username || '?').charAt(0)}</span>
              <span className="wz-user-name">{user?.username}</span>
            </div>
            {user?.role_label && (
              <div className="wz-label">{user.role_label}</div>
            )}
            {canUpload && (
              <Button
                size="small"
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={() => setUploadOpen(true)}
                block
              >
                Projekte hochladen
              </Button>
            )}
            {isAdmin && (
              <Button
                size="small"
                icon={<TeamOutlined />}
                onClick={() => navigate('/admin')}
                block
              >
                Benutzerverwaltung
              </Button>
            )}
            <Button
              type="text"
              size="small"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="wz-logout-btn"
            >
              Abmelden
            </Button>
          </div>

          <div className={`wz-live-card ${autoSearch ? 'wz-live-card-on' : ''}`}>
            <div className="wz-live-card-row">
              <span className="wz-live-title">
                <span className={`wz-live-dot ${autoSearch ? '' : 'wz-live-off'}`} />
                Live-Suche
              </span>
              <Switch
                checked={autoSearch}
                onChange={setAutoSearch}
                checkedChildren="Ein"
                unCheckedChildren="Aus"
              />
            </div>
            <div className="wz-live-hint">
              {autoSearch
                ? 'Suche läuft automatisch bei Änderungen'
                : 'Manuelle Suche mit Button'}
            </div>
          </div>

          <Sidebar
            filters={filters}
            setFilters={setFilters}
            ebkpCodes={ebkpCodes}
            onContinueEstimation={handleContinueEstimation}
          />
        </div>
      </Sider>

      <Layout style={{ marginLeft: 300 }}>
        <Header className="wz-header wz-keyline">
          <img src="/logo_black.png" alt="Logo" className="wz-header-logo" />
          <span className="wz-header-divider" />
          <span className="wz-wordmark wz-header-wordmark">Wizzy</span>
        </Header>

        <Content className="wz-content">
          <h1 className="wz-page-title wz-wordmark">Wizzy</h1>

          <SearchOverview filters={filters} />

            {!autoSearch && (
              <div style={{ marginBottom: '40px' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                  className="search-button"
                  style={{ marginTop: '20px' }}
                >
                  Suchen
                </Button>
              </div>
            )}

            {/* Cost Estimation Button - Between Overview and Results */}
            {canEstimate && results && results.success && (
              <Button
                type="default"
                size="large"
                icon={<CalculatorOutlined />}
                onClick={handleOpenEstimation}
                className="wz-estimate-cta"
              >
                Kostenschätzung erstellen
              </Button>
            )}

          {loading && (
            <div className="wz-loading-panel">
              <Spin size="large" />
              <div className="wz-loading-text">
                Suche läuft...
              </div>
            </div>
          )}

          {results && (
            <div style={{ position: 'relative' }}>
              {loading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Spin size="large" />
                </div>
              )}
              <Results results={results} />
            </div>
          )}
        </Content>
      </Layout>
      {canUpload && (
        <ProjectUpload open={uploadOpen} onClose={() => setUploadOpen(false)} />
      )}
    </Layout>
  );
};

export default SearchInterface;